import {
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@musa.tech/design-system";
import { FaultReasonBar } from "./FaultReasonBar";
import type { FaultReasonBreakdown } from "@/integrations/agreements/client";

interface FaultReasonsListProps {
  reasons: FaultReasonBreakdown[];
}

/**
 * Lista "Motivos de falha" (Figma node 233:2690) — label + barra de proporção
 * por motivo. O rótulo é truncado em 104px (largura do design) e os motivos
 * reais do cadastro são bem mais longos que os do mock, então o texto completo
 * fica no tooltip. `tabIndex` deixa o tooltip alcançável por teclado, já que
 * um texto não é focável por padrão.
 */
export const FaultReasonsList = ({ reasons }: FaultReasonsListProps) => (
  <div className="flex w-full flex-col gap-2">
    <Text variant="label" size="small" className="font-semibold text-neutral-600">
      Motivos de falha
    </Text>

    {reasons.length === 0 ? (
      <Text variant="body" size="small" className="text-neutral-500">
        Nenhuma falha registrada no período
      </Text>
    ) : (
      <TooltipProvider>
        <div className="flex flex-col gap-2">
          {reasons.map((reason) => (
            <div
              key={reason.reason_id ?? reason.reason_label}
              className="flex h-4 w-full items-center gap-4"
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={0}
                    className="w-[104px] shrink-0 cursor-help truncate whitespace-nowrap text-xs leading-4 text-black"
                  >
                    {reason.reason_label}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-xs leading-relaxed">
                  {reason.reason_label} — {reason.count}{" "}
                  {reason.count === 1 ? "coleta" : "coletas"} (
                  {reason.percentage.toLocaleString("pt-BR", {
                    maximumFractionDigits: 1,
                  })}
                  % das falhas)
                </TooltipContent>
              </Tooltip>
              <FaultReasonBar percentage={reason.percentage} />
            </div>
          ))}
        </div>
      </TooltipProvider>
    )}
  </div>
);
